/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { at } from 'lodash';
import { useField } from 'formik';
import {
  Checkbox, FormControl, FormControlLabel, FormHelperText,
} from '@mui/material';
import PropTypes from 'prop-types';

/**
 * CheckboxField Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function CheckboxField(props) {
  const {
    label, required, checkedBefore, onChange, ...rest
  } = props;
  const [field, meta, helper] = useField(props);
  const { setValue } = helper;

  function renderHelperText() {
    const [touched, error] = at(meta, 'touched', 'error');
    if (touched && error) {
      return <FormHelperText>{error}</FormHelperText>;
    }
    return null;
  }

  function _onChange(e) {
    setValue(e.target.checked);
    if (typeof onChange === 'function') {
      onChange(e);
    }
  }

  return (
    <FormControl {...rest}>
      <FormControlLabel
        value={field.checked}
        checked={field.checked || (!field.checked && checkedBefore)}
        /* eslint-disable-next-line react/jsx-no-bind */
        control={<Checkbox {...field} onChange={_onChange} />}
        label={label}
        required={required}
        className="checkbox-label"
      />
      {renderHelperText()}
    </FormControl>
  );
}

CheckboxField.propTypes = {
  label: PropTypes.object.isRequired,
  required: PropTypes.bool,
  checkedBefore: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

CheckboxField.defaultProps = {
  required: false,
};
